import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { fetchByIds, fetchOrgMembers } from "@/lib/large-org";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { useIncident, useCreateFollowup } from "@/lib/incidents";

export const Route = createFileRoute("/_authenticated/incidents/$incidentId/follow-up")({
  component: FollowUpPage,
  head: () => ({ meta: [{ title: "Follow-up — MissionReady" }] }),
});

function FollowUpPage() {
  const { incidentId } = Route.useParams();
  const { currentOrg, currentRole, user } = useAuth();
  const navigate = useNavigate();
  const { data: incident } = useIncident(incidentId);
  const createFollowup = useCreateFollowup(
    incidentId,
    currentOrg?.id ?? "",
    user?.id ?? "",
  );

  const isStaff = ["org_owner", "admin", "supervisor", "dispatcher"].includes(currentRole ?? "");
  const [members, setMembers] = useState<{ user_id: string; role: string; full_name?: string | null; email?: string | null }[]>([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    assignee_id: "__unassigned",
    priority: "normal",
    due_at: "",
  });

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const ms = await fetchOrgMembers<{ user_id: string; role: string }>(currentOrg.id, "user_id, role");
      const ids = ((ms as any[]) ?? []).map((m) => m.user_id);
      const profs = ids.length ? await fetchByIds<any>("profiles", "id, full_name, email", ids) : [];
      const profMap = new Map((profs ?? []).map((p: any) => [p.id, p]));
      setMembers(((ms as any[]) ?? []).map((m) => ({
        ...m,
        full_name: profMap.get(m.user_id)?.full_name,
        email: profMap.get(m.user_id)?.email,
      })));
    })();
  }, [currentOrg?.id]);

  if (!isStaff) {
    return (
      <div className="px-4 py-12 text-center text-sm text-muted-foreground">
        Only supervisors, dispatchers, or admins can create follow-ups.
      </div>
    );
  }

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      await createFollowup.mutateAsync({
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        assignee_id: form.assignee_id === "__unassigned" ? null : form.assignee_id,
        priority: form.priority as any,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
      });
      toast.success("Follow-up created");
      navigate({ to: "/incidents/$incidentId", params: { incidentId } });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to create follow-up");
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/incidents/$incidentId" params={{ incidentId }}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to incident
        </Link>
      </Button>

      <div className="flex items-center gap-2">
        <ListChecks className="h-5 w-5 text-primary" />
        <h1 className="font-display text-2xl font-bold md:text-3xl">New follow-up</h1>
      </div>
      {incident && (
        <p className="mt-1 text-sm text-muted-foreground">
          On <span className="font-mono text-xs">{incident.reference_number}</span>
          {incident.title ? ` · ${incident.title}` : ""}
        </p>
      )}

      <div className="mt-6 grid gap-4 rounded-xl border border-border bg-card p-5">
        <div className="grid gap-1.5">
          <Label>Title *</Label>
          <Input aria-label="Title *"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Re-interview witness, contact site manager"
          />
        </div>

        <div className="grid gap-1.5">
          <Label>Details</Label>
          <Textarea aria-label="Details"
            rows={5}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What needs to be done and why."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Assignee</Label>
            <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
              <SelectTrigger aria-label="Assignee"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__unassigned">Unassigned</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.full_name || m.email || m.user_id.slice(0, 8)} · {m.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger aria-label="Priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["low", "normal", "high", "urgent", "critical"].map((p) => (
                  <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label>Due</Label>
          <Input aria-label="Due" type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
        </div>

        <div className="flex justify-end gap-2">
          <Button asChild variant="outline">
            <Link to="/incidents/$incidentId" params={{ incidentId }}>Cancel</Link>
          </Button>
          <Button onClick={submit} disabled={createFollowup.isPending}>
            {createFollowup.isPending ? "Creating…" : "Create follow-up"}
          </Button>
        </div>
      </div>
    </div>
  );
}
